/**
 * The error banner over the prototype panel: what it says and what the founder can do about it.
 *
 * Its one action used to be "Cancel", wired to stopping the build. With no build running the
 * click did nothing and the banner never left (Bug 717: "Could not save prototype snapshot",
 * 8 Oct 2026). And a banner raised when the connection dropped stayed over the same build after
 * it carried on (Bug 716: "The prototype build failed. Try again." above "Checking it works").
 */

import { userMessageFor } from './buildStreamErrors';

/** Shown when a finished build's snapshot could not be saved. */
export const SNAPSHOT_SAVE_FAILED_MESSAGE = 'Could not save prototype snapshot. Changes may not persist.';

export interface WorkbenchBanner {
  message: string;
  /** A build is streaming, so there is something to stop. */
  canStop: boolean;
  /**
   * The turn whose build this banner is about, when it stopped and can be built again from the
   * last finished version (Task 722). Absent while a build runs, and for any other message.
   */
  rebuildTurnId?: string;
}

/** The banner to show, or null when there is nothing to report. It can always be dismissed. */
export function workbenchBannerFor(
  message: string | null | undefined,
  state: { isStreaming: boolean; rebuildTurnId?: string | null },
): WorkbenchBanner | null {
  if (!message) return null;
  return {
    message,
    canStop: state.isStreaming,
    ...(!state.isStreaming && state.rebuildTurnId && isBuildStoppedMessage(message)
      ? { rebuildTurnId: state.rebuildTurnId }
      : {}),
  };
}

/** The stream errors that mean the build stopped: the builder went away, failed, or timed out. */
const BUILD_STOPPED_CODES = ['CHEF_UNAVAILABLE', 'INTERNAL', 'TIMEOUT'] as const;

export function isBuildStoppedMessage(message: string): boolean {
  return BUILD_STOPPED_CODES.some((code) => userMessageFor(code) === message);
}

/**
 * The banner once the build it complained about is moving again: a stream error is dropped, any
 * other message stays. `streamErrorMessage` is the message the stream error raised, or null when
 * the banner on screen did not come from one.
 */
export function bannerAfterProgress(current: string | null, streamErrorMessage: string | null): string | null {
  return streamErrorMessage !== null && current === streamErrorMessage ? null : current;
}
